import ArrowRightIcon from '@mui/icons-material/ArrowRightRounded';
import TuneIcon from '@mui/icons-material/Tune';
import { IconButton, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import { menuOpeningProps } from 'components/brand-voice/menu-opening-position-props';
import {
  PotentialDisabledMenuItem,
  PotentialDisabledMenuItemText,
  UpgradeButton,
} from 'features/aiImages/AiImagesPage/ImageQualitySelect';
import { useShowTemplatesModal } from 'features/aiWriter/commandTemplates/useShowTemplatesModal';
import { useUpdateCommandTemplateUsage } from 'features/aiWriter/commandTemplates/useUpdateCommandTemplateUsage';
import FormattedMessage from 'features/i18n/FormattedMessage';
import { PricingPlan } from 'features/pricing/types';
import { trackingWrapper } from 'features/tracking/wrapper/TrackingWrapper';
import useUpdateSubscriptionModal from 'features/updateSubscriptionModal/hook/useUpdateSubscriptionModal';
import { MouseEvent, useState } from 'react';
import {
  GPT_MODELS,
  GptModel,
} from 'services/backofficeIntegration/http/endpoints/aiWriter/httpCreateConversation';
import { GAEvents } from 'services/tracking/GAEvents';
import gtmIds from 'services/tracking/GTMIds';
import { withGtmInteraction } from 'services/tracking/withGtmInteraction';
import styled from 'styled-components';
import useTr from 'utils/hooks/useTr';
import { withTestId } from 'utils/utils';

export const allowedPricingPlans: Array<PricingPlan> = [
  PricingPlan.pro,
  PricingPlan.premium,
  PricingPlan.business,
  PricingPlan.enterprise,
];

type Props = {
  gptModel: GptModel;
  isGpt4Disabled: boolean;
  modelId: string;
  isResetChatDisabled?: boolean;
  menuOpeningDirection?: 'top' | 'bottom';
  onGptModelChange: (gptModel: GptModel) => void;
  onMouseDown?: (event: MouseEvent) => void;
  onResetChat?: () => void;
  onSettingsChange?: () => void;
  onTemplateSelection: (template: string) => void;
};

export const ChatSettingsPopover = ({
  gptModel,
  isGpt4Disabled,
  modelId,
  isResetChatDisabled,
  menuOpeningDirection = 'bottom',
  onGptModelChange,
  onMouseDown,
  onResetChat,
  onSettingsChange,
  onTemplateSelection,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState<null | HTMLElement>(null);
  const showUpgradeSubscriptionModal = useUpdateSubscriptionModal();
  const showTemplatesModal = useShowTemplatesModal();
  const { mutate: updateCommandTemplateUsage } = useUpdateCommandTemplateUsage();
  const translate = useTr();

  const open = Boolean(anchorEl);
  const submenuOpen = Boolean(submenuAnchorEl);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSubmenuAnchorEl(null);
  };

  const handleProfileClick = (event: MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    if (submenuOpen) {
      setSubmenuAnchorEl(null);
    } else {
      GAEvents.overviewGptSelectOpen();
      setSubmenuAnchorEl(event.currentTarget);
    }
  };

  const handleUpgradeClick = () => {
    showUpgradeSubscriptionModal();
  };

  const handleGptModelSelect = (selectedModel: GptModel) => {
    onGptModelChange(selectedModel);
    handleClose();
    onSettingsChange?.();
  };

  const handleTemplateSelect = () => {
    trackingWrapper.track('templatesOpenedViaChat');
    showTemplatesModal({
      preselectedModelId: modelId,
      onTemplateSelect: dto => {
        trackingWrapper.track('templateSelected', {
          templateId: dto.id,
          templateLabel: dto.title,
          templateCountry: dto.country,
          templateLanguage: dto.language,
        });

        updateCommandTemplateUsage({ templateId: dto.id });
        onTemplateSelection(dto.template);
        GAEvents.promptInChatSent();
      },
    });
    handleClose();
    onSettingsChange?.();
  };

  const handleResetChat = () => {
    handleClose();
    onResetChat?.();
  };

  return (
    <>
      <Tooltip title={translate('chat.message_input.settings.tooltip')}>
        <StyledIconButton onClick={handleClick} onMouseDown={onMouseDown}>
          <StyledTuneIcon />
        </StyledIconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        {...menuOpeningProps[menuOpeningDirection]}
      >
        <MenuItem onClick={handleProfileClick}>
          <ListItemText>
            <FormattedMessage id="chat.message_input.settings_menu.gpt" />
          </ListItemText>
          <StyledArrowRightIcon fontSize="small" />
        </MenuItem>
        <MenuItem
          onClick={handleTemplateSelect}
          {...withGtmInteraction(gtmIds.aiWriter.projectOverview.chat.openPromptLibrary)}
        >
          <ListItemText>
            <FormattedMessage id="chat.message_input.settings_menu.prompt_library" />
          </ListItemText>
        </MenuItem>

        {onResetChat && (
          <MenuItem
            {...withGtmInteraction(gtmIds.aiWriter.chat.reset)}
            {...withTestId('chat-reset-button')}
            onClick={handleResetChat}
            disabled={isResetChatDisabled}
          >
            <ListItemText>
              <FormattedMessage id="chat.message_input.settings_menu.reset_chat" />
            </ListItemText>
          </MenuItem>
        )}
      </Menu>

      <Menu
        anchorEl={submenuAnchorEl}
        open={submenuOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <PotentialDisabledMenuItem
          {...withGtmInteraction(gtmIds.aiWriter.chat.selectGptModel)}
          selected={gptModel === GPT_MODELS.GPT_3_5}
          onClick={() => {
            handleGptModelSelect(GPT_MODELS.GPT_3_5);
            onSettingsChange?.();
          }}
        >
          <FormattedMessage id="aiWriter.inspirations.chat.change_GPT_model.GPT_3_5" />
        </PotentialDisabledMenuItem>
        <PotentialDisabledMenuItem
          {...withGtmInteraction(gtmIds.aiWriter.chat.selectGptModel)}
          selected={gptModel === GPT_MODELS.GPT_4}
          onClick={() => {
            handleGptModelSelect(GPT_MODELS.GPT_4);
            onSettingsChange?.();
          }}
        >
          <PotentialDisabledMenuItemText>
            <FormattedMessage id="aiWriter.inspirations.chat.change_GPT_model.GPT_4" />
            {isGpt4Disabled && <UpgradeButton onClick={handleUpgradeClick} />}
          </PotentialDisabledMenuItemText>
        </PotentialDisabledMenuItem>
      </Menu>
    </>
  );
};

const StyledArrowRightIcon = styled(ArrowRightIcon)`
  color: ${({ theme }) => theme.colors.componentsIconDefault};
`;

const StyledIconButton = styled(IconButton)`
  border: 1px solid ${({ theme }) => theme.colors.divider};
  width: 34px;
  height: 34px;
`;

const StyledTuneIcon = styled(TuneIcon)`
  color: ${({ theme }) => theme.colors.componentsIconDefault};
`;
